import React, { FC, ReactNode, useEffect, useRef } from 'react';
import s from './index.scss';
import { Image } from '@wix/design-system';
import { Dismiss } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import { Asset } from '@wix/ambassador-funnel-templates-gallery-v2-template/types';
import { modalShown, reportEditorClick } from '../../utils/bi';

interface ITemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: ReactNode;
  image?: Asset;
  editorUrl: string;
}

export const TemplateModal: FC<ITemplateModalProps> = ({ isOpen, onClose, image, editorUrl }) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  const onEditorClick = () => {
    onClose();
    reportEditorClick({ origin: 'edit_modal' });
  };

  useEffect(() => {
    const dialog = dialogRef.current;

    if (!dialog) {
      return;
    }

    if (isOpen) {
      modalShown();
      dialog?.showModal();
    } else {
      dialog?.close();
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const dialog = dialogRef?.current;
    if (!dialog) {
      return;
    }

    const rect = dialog.getBoundingClientRect();
    const isInDialog =
      rect.top <= e.clientY &&
      e.clientY <= rect.top + rect.height &&
      rect.left <= e.clientX &&
      e.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      onClose();
    }
  };

  return (
    <dialog ref={dialogRef} className={s.TemplateModal} onClick={handleOverlayClick}>
      <div className={s.TemplateModal__content}>
        <button className={s.TemplateModal__close} onClick={onClose}>
          <Dismiss />
        </button>
        <span className={s.TemplateModal__title}>{t('template-modal.title')}</span>
        <div
          className={s.TemplateModal__webContainer}
          style={image?.width && image?.height ? { aspectRatio: `${image?.width} / ${image?.height}` } : {}}
        >
          <div className={s.TemplateModal__webDotContainer}>
            <span className={s.TemplateModal__webDot} />
            <span className={s.TemplateModal__webDot} />
            <span className={s.TemplateModal__webDot} />
          </div>
          <Image className={s.TemplateModal__img} src={image?.absoluteUrl} alt={image?.alt} />
        </div>
        <span className={s.TemplateModal__description}>{t('template-modal.description')}</span>
        <a target="_blank" href={editorUrl} onClick={onEditorClick} className={s.TemplateModal__btn} rel="noreferrer">
          {t('template-modal.btn')}
        </a>
      </div>
    </dialog>
  );
};
